import React from 'react';
import { useUser } from './UserContext';
import { useFilter } from '../components/Filter/ArtworkFilterContext';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ClientAccess } from '../models/UserProfile';

type ClientSelectorProps = {
    inMenuBar: boolean;
};


type ClientHealthState = 'none' | 'info' | 'warning' | 'error';

function getPercent(used: number, max: number): number {
    if (!max || max <= 0) return 0;
    return Math.round((used / max) * 100);
}

function getClientHealthState(client: ClientAccess): ClientHealthState {
    const access = client.licenseAccess;
    if (!access) return 'none';

    const portfolioPercent = getPercent(access.portfoliosUsed, access.portfoliosMax);
    const artworkPercent = getPercent(access.artworksUsed, access.artworksMax);

    const portfolioLimitReached = access.portfoliosMax > 0 && access.portfoliosUsed >= access.portfoliosMax;
    const artworkLimitReached = access.artworksMax > 0 && access.artworksUsed >= access.artworksMax;

    if (access.licenseExpired || portfolioLimitReached || artworkLimitReached) {
        return 'error';
    }

    if (
        (access.daysRemaining > 0 && access.daysRemaining <= 14) ||
        portfolioPercent >= 85 ||
        artworkPercent >= 85
    ) {
        return 'warning';
    }

    if (portfolioPercent >= 70 || artworkPercent >= 70) {
        return 'info';
    }

    return 'none';
}

function getClientHealthIcon(state: ClientHealthState) {
    switch (state) {
        case 'error':
            return <ErrorIcon fontSize="small" color="error" />;
        case 'warning':
            return <WarningAmberIcon fontSize="small" color="warning" />;
        case 'info':
            return <InfoOutlinedIcon fontSize="small" color="info" />;
        default:
            return null;
    }
}

function getClientHealthTitle(client: ClientAccess): string | undefined {
    const access = client.licenseAccess;
    if (!access) return undefined;

    const portfolioPercent = getPercent(access.portfoliosUsed, access.portfoliosMax);
    const artworkPercent = getPercent(access.artworksUsed, access.artworksMax);

    if (access.licenseExpired) {
        return 'Subscription expired';
    }

    if (access.artworksMax > 0 && access.artworksUsed >= access.artworksMax) {
        return 'Artwork limit reached';
    }

    if (access.portfoliosMax > 0 && access.portfoliosUsed >= access.portfoliosMax) {
        return 'Portfolio limit reached';
    }

    if (access.daysRemaining > 0 && access.daysRemaining <= 14) {
        return access.daysRemaining === 1
            ? 'Subscription expires tomorrow'
            : `Subscription expires in ${access.daysRemaining} days`;
    }

    if (artworkPercent >= 85) {
        return `Artwork usage ${artworkPercent}%`;
    }

    if (portfolioPercent >= 85) {
        return `Portfolio usage ${portfolioPercent}%`;
    }

    if (artworkPercent >= 70) {
        return `Artwork usage ${artworkPercent}%`;
    }

    if (portfolioPercent >= 70) {
        return `Portfolio usage ${portfolioPercent}%`;
    }

    return undefined;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({ inMenuBar }) => {
    const { user, activeClientId, setActiveClientId, activeClient } = useUser();
    const { resetFilter } = useFilter();

    if (!user || !user.clients || user.clients.length === 0) {
        return <p>No user data available.</p>;
    }

    const handleClientChange = (newClientId: number) => {
        setActiveClientId(newClientId);
        resetFilter(newClientId);
    }

    return (
        <div className={inMenuBar ? "menu-bar-style" : "side-menu-style"}>
            <div className="client-select-box">
                <FormControl fullWidth size="small" className="client-selector">
                    <InputLabel
                        sx={{
                            color: 'white',
                            backgroundColor: '#4a4444',
                            display: { xs: 'none', sm: 'inline-block' }
                        }}
                    >
                        Collection
                    </InputLabel>

                    <Select
                        labelId="client-selector-label"
                        value={activeClientId || ''}
                        onChange={(e) => handleClientChange(Number(e.target.value))}
                        renderValue={(selected) => {
                            const selectedClient = user?.clients?.find(
                                (c: ClientAccess) => c.clientId === Number(selected)
                            ) as ClientAccess | undefined;

                            if (!selectedClient) return '';

                            const state = getClientHealthState(selectedClient);
                            const icon = getClientHealthIcon(state);

                            return (
                                <Box display="flex" alignItems="center" gap={1}>
                                    {icon}
                                    <Typography variant="body2" noWrap>
                                        {selectedClient.clientName}
                                    </Typography>
                                </Box>
                            );
                        }}
                    >
                        {user.clients.map((client: ClientAccess) => {
                            const state = getClientHealthState(client);
                            const icon = getClientHealthIcon(state);
                            const helperText = getClientHealthTitle(client);

                            return (
                                <MenuItem key={client.clientId} value={client.clientId}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        width="100%"
                                        gap={2}
                                    >
                                        <Box minWidth={0}>
                                            <Typography variant="body2" noWrap>
                                                {client.clientName}
                                            </Typography>
                                            {helperText && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block' }}
                                                    noWrap
                                                >
                                                    {helperText}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box display="flex" alignItems="center" flexShrink={0}>
                                            {icon}
                                        </Box>
                                    </Box>
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </div>
        </div>
    );
};

export default ClientSelector;